import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Sparkles } from "lucide-react";
import { scrollToId, WHATSAPP_URL } from "@/data/site";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-32 pb-16">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.05) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div aria-hidden className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#00F0FF]/20 blur-[120px]" />
      <div aria-hidden className="absolute top-1/3 right-0 h-80 w-80 rounded-full bg-[#FF9F00]/15 blur-[120px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
            className="mb-6 flex items-center gap-3 font-mono-sig text-xs font-bold uppercase tracking-[0.3em] text-[#1D4ED8]"
          >
            <span className="inline-block h-px w-10 bg-[#1D4ED8]" />
            Agencia de Marketing Digital · España
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-display text-5xl sm:text-7xl lg:text-8xl font-extrabold uppercase leading-[0.92] tracking-tight text-slate-900"
          >
            <MaskedLine delay={0.45}>Estrategia.</MaskedLine>
            <MaskedLine delay={0.58}>
              <span className="text-outline">Creatividad.</span>
            </MaskedLine>
            <MaskedLine delay={0.71}>
              <span className="text-[#1D4ED8]">Resultados</span>
              <span className="text-[#00F0FF]">.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
          >
            Tiendas online, diseño gráfico, redes sociales, Google Business y páginas web.
            Todo lo que tu negocio necesita para despegar, con precios claros y trato directo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-cta-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-4 text-sm font-bold text-white shadow-xl shadow-[#25D366]/30 transition-transform hover:scale-105 active:scale-95"
            >
              <MessageCircle className="h-5 w-5" />
              Hablar por WhatsApp
            </a>
            <button
              data-testid="hero-cta-form"
              onClick={() => scrollToId("#servicios")}
              className="group flex items-center gap-2.5 rounded-full border-2 border-slate-900 px-7 py-[14px] text-sm font-bold text-slate-900 transition-colors hover:bg-slate-900 hover:text-white"
            >
              Ver servicios
              <ArrowDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono-sig text-[11px] uppercase tracking-[0.2em] text-slate-500"
          >
            <span data-testid="hero-stat-prices" className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-[#FF9F00]" /> Precios transparentes
            </span>
            <span data-testid="hero-stat-permanencia">· Sin permanencia</span>
            <span data-testid="hero-stat-directo">· Trato directo</span>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: photoY }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.6 }}
            className="relative mx-auto max-w-sm"
          >
            <div aria-hidden className="absolute -inset-3 translate-x-4 translate-y-4 rounded-2xl border-2 border-[#00F0FF]" />
            <div
              data-testid="founder-photo-container"
              className="cyan-glow relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900"
            >
              <img
                src="/assets/founder.jpg"
                alt="Fundador de Socify Marketing"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-5 pt-16">
                <p className="font-mono-sig text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
                  Fundador & Estratega
                </p>
                <p data-testid="founder-name" className="font-display text-xl font-bold text-white">Abraham Carpio</p>
                <p className="text-xs text-slate-400">Socify Marketing</p>
              </div>
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -right-4 top-8 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl"
            >
              <p className="font-mono-sig text-[10px] uppercase tracking-widest text-slate-400">Respuesta</p>
              <p className="font-display text-sm font-bold text-slate-900">&lt; 2 h laborables</p>
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -left-6 bottom-16 rounded-xl bg-slate-900 px-4 py-3 shadow-xl"
            >
              <p className="font-mono-sig text-[10px] uppercase tracking-widest text-[#00F0FF]">@socify_marketing</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
